import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Alert,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Paper
} from '@mui/material';
import {
  ViewModule,
  Apps,
  Search as SearchIcon
} from '@mui/icons-material';

const BLOCK_SIZE = 500;

/**
 * Dialog for selecting a block of apps when there are more than 500.
 * Helps users render large datasets in manageable chunks for better performance.
 */
const BlockSelectionDialog = ({
  open,
  onClose,
  apps = [],
  onSelectBlock,
  onSelectAll
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedApps, setSelectedApps] = useState([]);
  const [searchText, setSearchText] = useState('');
  
  // Filter apps based on search text
  const filteredApps = useMemo(() => {
    if (!searchText.trim()) return apps;
    const searchLower = searchText.toLowerCase();
    return apps.filter(app => 
      app.displayName.toLowerCase().includes(searchLower) ||
      (app.appType && app.appType.toLowerCase().includes(searchLower))
    );
  }, [apps, searchText]);

  // Calculate blocks
  const blocks = useMemo(() => {
    const result = [];
    const totalApps = apps.length;
    const numBlocks = Math.ceil(totalApps / BLOCK_SIZE);
    
    for (let i = 0; i < numBlocks; i++) {
      const startIndex = i * BLOCK_SIZE;
      const endIndex = Math.min((i + 1) * BLOCK_SIZE, totalApps);
      const blockApps = apps.slice(startIndex, endIndex);
      
      // Get first and last app names for preview
      const firstApp = blockApps[0]?.displayName || 'Unknown';
      const lastApp = blockApps[blockApps.length - 1]?.displayName || 'Unknown';
      
      result.push({
        index: i,
        startIndex,
        endIndex,
        count: endIndex - startIndex,
        label: `Block ${i + 1}`,
        range: `${startIndex + 1} - ${endIndex}`,
        firstApp: firstApp.length > 30 ? firstApp.substring(0, 30) + '...' : firstApp,
        lastApp: lastApp.length > 30 ? lastApp.substring(0, 30) + '...' : lastApp,
        apps: blockApps
      });
    }
    
    return result;
  }, [apps]);

  const handleSelectBlock = (block) => {
    const selectedIds = block.apps.map(app => app.id);
    onSelectBlock(selectedIds, block);
    onClose();
  };

  const handleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll();
    }
    onClose();
  };

  const handleManualSelect = () => {
    if (selectedApps.length > 0) {
      onSelectBlock(selectedApps, {
        label: 'Manual Selection',
        count: selectedApps.length,
        range: `${selectedApps.length} apps selected`
      });
      onClose();
    }
  };

  const handleToggleApp = (appId) => {
    setSelectedApps(prevApps =>
      prevApps.includes(appId)
        ? prevApps.filter(id => id !== appId)
        : [...prevApps, appId]
    );
  };

  const handleToggleAll = () => {
    if (selectedApps.length === filteredApps.length && filteredApps.length > 0) {
      setSelectedApps([]);
    } else {
      setSelectedApps(filteredApps.map(app => app.id));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewModule color="primary" />
          <Typography variant="h6">
            Large Dataset Detected
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>{apps.length.toLocaleString()} applications</strong> detected. Choose which ones to include, then press Generate Diagram.
          </Typography>
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Quick Blocks" />
            <Tab label="Manual Selection" />
          </Tabs>
        </Box>

        {/* Tab 0: Quick Blocks */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Click a block to select it:
            </Typography>

            <Grid container spacing={2} sx={{ maxHeight: '350px', overflow: 'auto', pb: 1 }}>
              {blocks.map((block) => (
                <Grid item xs={12} sm={6} md={4} key={block.index}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: 2
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleSelectBlock(block)}
                      sx={{ height: '100%' }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6" color="primary">
                            {block.label}
                          </Typography>
                          <Chip 
                            label={`${block.count} apps`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Apps {block.range}
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          <Typography variant="caption" display="block" noWrap>
                            <strong>First:</strong> {block.firstApp}
                          </Typography>
                          <Typography variant="caption" display="block" noWrap>
                            <strong>Last:</strong> {block.lastApp}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Alert severity="warning" icon={<Apps />}>
              <Typography variant="body2">
                <strong>Include all {apps.length.toLocaleString()} apps?</strong> Diagrams this large are slow to
                generate and hard to read. Application icons are turned off automatically above {BLOCK_SIZE} apps
                to keep it manageable.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 1: Manual Selection */}
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search applications..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {selectedApps.length} of {filteredApps.length} selected
              </Typography>
              <Button 
                size="small"
                onClick={handleToggleAll}
                variant={selectedApps.length === filteredApps.length && filteredApps.length > 0 ? 'contained' : 'outlined'}
              >
                {selectedApps.length === filteredApps.length && filteredApps.length > 0 ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <Paper 
              sx={{ 
                maxHeight: '350px', 
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <List sx={{ width: '100%' }}>
                {filteredApps.map((app) => (
                  <ListItem 
                    key={app.id}
                    dense
                    button
                    onClick={() => handleToggleApp(app.id)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedApps.includes(app.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={app.displayName}
                      secondary={app.appType}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {tabValue === 0 && (
          <Button 
            onClick={handleSelectAll} 
            color="warning"
            variant="outlined"
          >
            Select all ({apps.length.toLocaleString()})
          </Button>
        )}
        {tabValue === 1 && (
          <Button 
            onClick={handleManualSelect}
            variant="contained"
            disabled={selectedApps.length === 0}
          >
            Use selection ({selectedApps.length})
          </Button>
        )}   
      </DialogActions>
    </Dialog>
  );
};

export default BlockSelectionDialog;
